import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import SideSheet from "@/components/SideSheet";
import { Button } from "@/components/ui/button";
import { DetailSkeleton } from "@/components/Skeletons";
import { fmtDate } from "@/lib/dates";
import { hhmm, joinMeta, plural } from "@/lib/utils";
import { statusClass, ATTENDANCE_TEXT_CLASS } from "@/lib/status";
import { useAuth, canWrite } from "@/context/AuthContext";
import { ExternalLink, Edit3, ClipboardCheck, FileCheck2, FileWarning } from "lucide-react";

const ATTENDANCE_ORDER = ["Present", "Late", "Absent", "Not Marked"];

function Row({ label, children, testid }) {
  return (
    <div className="grid grid-cols-[7rem_1fr] gap-3 text-sm py-1.5" data-testid={testid}>
      <div className="text-muted-foreground">{label}</div>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

/**
 * One training, read without leaving the list it was found in.
 *
 * Opened by a row of the register or a day on the calendar, with the id in the URL
 * (`?open=`) so the panel survives a refresh and travels in a pasted link. It answers
 * the questions a row cannot — who turned up, whether certificates went out, whether
 * the signed sheet is on file — and hands over to the full page for anything more.
 */
export default function TrainingPeek({ id, onClose }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const writable = canWrite(user);
  const [t, setT] = useState(null);

  useEffect(() => {
    if (!id) return undefined;
    let live = true;
    setT(null);
    api.get(`/trainings/${id}`)
      .then((r) => { if (live) setT(r.data); })
      .catch((e) => {
        toast.error(formatApiError(e.response?.data?.detail, "That training could not be opened"));
        onClose();
      });
    return () => { live = false; };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const participants = t?.participants || [];
  const counts = Object.fromEntries(ATTENDANCE_ORDER.map((s) => [s, 0]));
  participants.forEach((p) => { counts[p.attendance_status] = (counts[p.attendance_status] || 0) + 1; });
  const issued = participants.filter((p) => p.certificate_number && !p.certificate_revoked_at).length;
  const editable = writable && ["Draft", "Scheduled"].includes(t?.workflow_status);
  const markable = writable && ["Scheduled", "Completed"].includes(t?.workflow_status);
  const sheet = t?.attendance_sheet;

  return (
    <SideSheet open={!!id} onOpenChange={(v) => !v && onClose()} testid="training-peek"
               title={t?.title || "Training"}
               description={t ? joinMeta(t.category, t.training_type, fmtDate(t.training_date)) : "Loading"}
               footer={t && (
                 <>
                   {markable && (
                     <Button variant="outline" onClick={() => nav(`/trainings/${id}?tab=attendance`)} data-testid="peek-attendance">
                       <ClipboardCheck className="w-4 h-4 mr-1.5" /> Attendance
                     </Button>
                   )}
                   {editable && (
                     <Button variant="outline" onClick={() => nav(`/trainings/${id}/edit`)} data-testid="peek-edit">
                       <Edit3 className="w-4 h-4 mr-1.5" /> Edit
                     </Button>
                   )}
                   <Button onClick={() => nav(`/trainings/${id}`)} data-testid="peek-open">
                     <ExternalLink className="w-4 h-4 mr-1.5" /> Open training
                   </Button>
                 </>
               )}>
      {!t ? <DetailSkeleton testid="training-peek-skeleton" /> : (
        <div className="space-y-5">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`px-2 py-1 rounded text-xs font-medium ${statusClass(t.workflow_status)}`} data-testid="peek-status">
              {t.workflow_status}
            </span>
            {t.workflow_status === "Completed" && (
              sheet ? (
                <span className="inline-flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-400" data-testid="peek-sheet-ok">
                  <FileCheck2 className="w-3.5 h-3.5" /> Signed sheet on file
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs text-amber-700 dark:text-amber-400" data-testid="peek-sheet-missing">
                  <FileWarning className="w-3.5 h-3.5" /> Signed sheet not uploaded
                </span>
              )
            )}
          </div>

          <section>
            <div className="overline text-muted-foreground mb-1">When & where</div>
            <Row label="Date">{fmtDate(t.training_date) || <span className="text-muted-foreground">Not set</span>}</Row>
            <Row label="Time">{joinMeta(t.start_time && t.end_time && `${hhmm(t.start_time)}–${hhmm(t.end_time)}`, t.duration_hours && `${t.duration_hours} hrs`) || "—"}</Row>
            {/* Two rows, not one. These were joined under the single label "Venue", so a
                training with no venue rendered its MODE under that word — "Venue:
                Classroom" — and a room and a delivery method are not the same fact. Same
                shape as the attendance sheet that once called two things "Unit". */}
            <Row label="Venue">{t.venue || "—"}</Row>
            <Row label="Mode">{t.mode || "—"}</Row>
          </section>

          <section>
            <div className="overline text-muted-foreground mb-1">Held for</div>
            <Row label="Units" testid="peek-units">
              {(t.unit_names || []).length ? (
                <ul className="space-y-0.5">
                  {(t.unit_ids || []).map((uid, i) => (
                    <li key={uid}>
                      {t.unit_names[i]}
                      {uid === t.unit_id && (t.unit_ids || []).length > 1 && (
                        <span className="ml-1.5 text-xs text-muted-foreground">named on certificates</span>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (t.unit_name || "—")}
            </Row>
            <Row label="Departments">{(t.department_names || []).join(", ") || <span className="text-muted-foreground">Whole unit</span>}</Row>
            <Row label="Trainer">{joinMeta(t.trainer_name, t.trainer_organization) || <span className="text-amber-700 dark:text-amber-400">Not recorded</span>}</Row>
          </section>

          <section>
            <div className="overline text-muted-foreground mb-2">Attendance</div>
            <div className="grid grid-cols-4 gap-2" data-testid="peek-attendance-counts">
              {ATTENDANCE_ORDER.map((s) => (
                <div key={s} className="rounded-md border border-border px-2 py-1.5 text-center">
                  <div className={`font-heading font-bold text-lg ${ATTENDANCE_TEXT_CLASS[s]}`}>{counts[s] || 0}</div>
                  <div className="text-[11px] text-muted-foreground">{s}</div>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {plural(participants.length, "person", "people")} nominated
              {issued ? ` · ${plural(issued, "certificate")} issued` : ""}
              {(t.attachments || []).length ? ` · ${plural(t.attachments.length, "file")}` : ""}
            </p>
          </section>

          {t.description && (
            <section>
              <div className="overline text-muted-foreground mb-1">Description</div>
              <p className="text-sm whitespace-pre-line">{t.description}</p>
            </section>
          )}
        </div>
      )}
    </SideSheet>
  );
}
